import { THEME_CSS_VARS } from './tokens';

type ThemePalette = {
  surface: string;
  surfaceAlt: string;
  accent: string;
  accentSoft: string;
  borderStrong: string;
  glowPrimary: string;
  glowSecondary: string;
  custom1: string;
  custom2: string;
  background: string;
  cardBackground: string;
  text: string;
  textSecondary: string;
  border: string;
  shadow: string;
};

type ThemeStatusPalette = {
  success: string;
  successSoft: string;
  successText: string;
  warning: string;
  warningSoft: string;
  warningText: string;
  error: string;
  errorSoft: string;
  errorText: string;
  info: string;
  infoSoft: string;
  infoText: string;
};

export type ThemeSettings = {
  version: number;
  updatedAt?: string | null;
  updatedBy?: number | string | null;
  primary: string;
  primaryHover: string;
  light: ThemePalette;
  dark: ThemePalette;
  status: ThemeStatusPalette;
};

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  version: 1,
  updatedAt: null,
  updatedBy: null,
  primary: '#B2140F',
  primaryHover: '#8D100C',
  light: {
    surface: '#FFFFFF',
    surfaceAlt: '#F2F4F8',
    accent: '#B2140F',
    accentSoft: 'rgba(178, 20, 15, 0.12)',
    borderStrong: 'rgba(0, 0, 0, 0.28)',
    glowPrimary: 'rgba(178, 20, 15, 0.12)',
    glowSecondary: 'rgba(13, 53, 89, 0.08)',
    custom1: '#0D3559',
    custom2: '#FFB020',
    background: 'linear-gradient(160deg, #FAFAFA 0%, #F5F5F5 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.98)',
    text: '#000000',
    textSecondary: '#616161',
    border: 'rgba(0, 0, 0, 0.12)',
    shadow: '0 20px 46px rgba(9, 32, 56, 0.22)'
  },
  dark: {
    surface: 'rgba(20, 28, 40, 0.95)',
    surfaceAlt: 'rgba(30, 40, 56, 0.95)',
    accent: '#E84C3D',
    accentSoft: 'rgba(232, 76, 61, 0.18)',
    borderStrong: 'rgba(255, 255, 255, 0.28)',
    glowPrimary: 'rgba(232, 76, 61, 0.2)',
    glowSecondary: 'rgba(35, 60, 90, 0.35)',
    custom1: '#0D3559',
    custom2: '#1f9aff',
    background: 'linear-gradient(160deg, #0a1929 0%, #1a2332 100%)',
    cardBackground: 'rgba(25, 35, 50, 0.95)',
    text: '#FFFFFF',
    textSecondary: '#B0BEC5',
    border: 'rgba(255, 255, 255, 0.12)',
    shadow: '0 20px 46px rgba(0, 0, 0, 0.5)'
  },
  status: {
    success: '#2e7d32',
    successSoft: '#e7f4ea',
    successText: '#1b5e20',
    warning: '#ed6c02',
    warningSoft: '#fff4e5',
    warningText: '#7a3d00',
    error: '#d32f2f',
    errorSoft: '#fdecea',
    errorText: '#b71c1c',
    info: '#0288d1',
    infoSoft: '#e3f2fd',
    infoText: '#01579b'
  }
};

const safeValue = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (trimmed.length > 200) return fallback;
  return trimmed;
};

const normalizePalette = (input: Partial<ThemePalette> | undefined, fallback: ThemePalette): ThemePalette => ({
  surface: safeValue(input?.surface, fallback.surface),
  surfaceAlt: safeValue(input?.surfaceAlt, fallback.surfaceAlt),
  accent: safeValue(input?.accent, fallback.accent),
  accentSoft: safeValue(input?.accentSoft, fallback.accentSoft),
  borderStrong: safeValue(input?.borderStrong, fallback.borderStrong),
  glowPrimary: safeValue(input?.glowPrimary, fallback.glowPrimary),
  glowSecondary: safeValue(input?.glowSecondary, fallback.glowSecondary),
  custom1: safeValue(input?.custom1, fallback.custom1),
  custom2: safeValue(input?.custom2, fallback.custom2),
  background: safeValue(input?.background, fallback.background),
  cardBackground: safeValue(input?.cardBackground, fallback.cardBackground),
  text: safeValue(input?.text, fallback.text),
  textSecondary: safeValue(input?.textSecondary, fallback.textSecondary),
  border: safeValue(input?.border, fallback.border),
  shadow: safeValue(input?.shadow, fallback.shadow)
});

const normalizeStatusPalette = (input: Partial<ThemeStatusPalette> | undefined, fallback: ThemeStatusPalette): ThemeStatusPalette => ({
  success: safeValue(input?.success, fallback.success),
  successSoft: safeValue(input?.successSoft, fallback.successSoft),
  successText: safeValue(input?.successText, fallback.successText),
  warning: safeValue(input?.warning, fallback.warning),
  warningSoft: safeValue(input?.warningSoft, fallback.warningSoft),
  warningText: safeValue(input?.warningText, fallback.warningText),
  error: safeValue(input?.error, fallback.error),
  errorSoft: safeValue(input?.errorSoft, fallback.errorSoft),
  errorText: safeValue(input?.errorText, fallback.errorText),
  info: safeValue(input?.info, fallback.info),
  infoSoft: safeValue(input?.infoSoft, fallback.infoSoft),
  infoText: safeValue(input?.infoText, fallback.infoText)
});

export const normalizeThemeSettings = (input?: Partial<ThemeSettings> | null): ThemeSettings => ({
  version: typeof input?.version === 'number' ? input?.version ?? 1 : DEFAULT_THEME_SETTINGS.version,
  updatedAt: input?.updatedAt ?? null,
  updatedBy: input?.updatedBy ?? null,
  primary: safeValue(input?.primary, DEFAULT_THEME_SETTINGS.primary),
  primaryHover: safeValue(input?.primaryHover, DEFAULT_THEME_SETTINGS.primaryHover),
  light: normalizePalette(input?.light, DEFAULT_THEME_SETTINGS.light),
  dark: normalizePalette(input?.dark, DEFAULT_THEME_SETTINGS.dark),
  status: normalizeStatusPalette(input?.status, DEFAULT_THEME_SETTINGS.status)
});

export const applyThemeSettings = (input?: Partial<ThemeSettings> | null): ThemeSettings => {
  const normalized = normalizeThemeSettings(input);
  if (typeof document === 'undefined') return normalized;
  const root = document.documentElement;
  root.style.setProperty(THEME_CSS_VARS.primary, normalized.primary);
  root.style.setProperty(THEME_CSS_VARS.primaryHover, normalized.primaryHover);
  root.style.setProperty(THEME_CSS_VARS.lightSurface, normalized.light.surface);
  root.style.setProperty(THEME_CSS_VARS.lightSurfaceAlt, normalized.light.surfaceAlt);
  root.style.setProperty(THEME_CSS_VARS.lightAccent, normalized.light.accent);
  root.style.setProperty(THEME_CSS_VARS.lightAccentSoft, normalized.light.accentSoft);
  root.style.setProperty(THEME_CSS_VARS.lightBorderStrong, normalized.light.borderStrong);
  root.style.setProperty(THEME_CSS_VARS.lightGlowPrimary, normalized.light.glowPrimary);
  root.style.setProperty(THEME_CSS_VARS.lightGlowSecondary, normalized.light.glowSecondary);
  root.style.setProperty(THEME_CSS_VARS.lightCustom1, normalized.light.custom1);
  root.style.setProperty(THEME_CSS_VARS.lightCustom2, normalized.light.custom2);
  root.style.setProperty(THEME_CSS_VARS.lightBackground, normalized.light.background);
  root.style.setProperty(THEME_CSS_VARS.lightCardBackground, normalized.light.cardBackground);
  root.style.setProperty(THEME_CSS_VARS.lightText, normalized.light.text);
  root.style.setProperty(THEME_CSS_VARS.lightTextSecondary, normalized.light.textSecondary);
  root.style.setProperty(THEME_CSS_VARS.lightBorder, normalized.light.border);
  root.style.setProperty(THEME_CSS_VARS.lightShadow, normalized.light.shadow);
  root.style.setProperty(THEME_CSS_VARS.darkSurface, normalized.dark.surface);
  root.style.setProperty(THEME_CSS_VARS.darkSurfaceAlt, normalized.dark.surfaceAlt);
  root.style.setProperty(THEME_CSS_VARS.darkAccent, normalized.dark.accent);
  root.style.setProperty(THEME_CSS_VARS.darkAccentSoft, normalized.dark.accentSoft);
  root.style.setProperty(THEME_CSS_VARS.darkBorderStrong, normalized.dark.borderStrong);
  root.style.setProperty(THEME_CSS_VARS.darkGlowPrimary, normalized.dark.glowPrimary);
  root.style.setProperty(THEME_CSS_VARS.darkGlowSecondary, normalized.dark.glowSecondary);
  root.style.setProperty(THEME_CSS_VARS.darkCustom1, normalized.dark.custom1);
  root.style.setProperty(THEME_CSS_VARS.darkCustom2, normalized.dark.custom2);
  root.style.setProperty(THEME_CSS_VARS.darkBackground, normalized.dark.background);
  root.style.setProperty(THEME_CSS_VARS.darkCardBackground, normalized.dark.cardBackground);
  root.style.setProperty(THEME_CSS_VARS.darkText, normalized.dark.text);
  root.style.setProperty(THEME_CSS_VARS.darkTextSecondary, normalized.dark.textSecondary);
  root.style.setProperty(THEME_CSS_VARS.darkBorder, normalized.dark.border);
  root.style.setProperty(THEME_CSS_VARS.darkShadow, normalized.dark.shadow);
  root.style.setProperty(THEME_CSS_VARS.statusSuccess, normalized.status.success);
  root.style.setProperty(THEME_CSS_VARS.statusSuccessSoft, normalized.status.successSoft);
  root.style.setProperty(THEME_CSS_VARS.statusSuccessText, normalized.status.successText);
  root.style.setProperty(THEME_CSS_VARS.statusWarning, normalized.status.warning);
  root.style.setProperty(THEME_CSS_VARS.statusWarningSoft, normalized.status.warningSoft);
  root.style.setProperty(THEME_CSS_VARS.statusWarningText, normalized.status.warningText);
  root.style.setProperty(THEME_CSS_VARS.statusError, normalized.status.error);
  root.style.setProperty(THEME_CSS_VARS.statusErrorSoft, normalized.status.errorSoft);
  root.style.setProperty(THEME_CSS_VARS.statusErrorText, normalized.status.errorText);
  root.style.setProperty(THEME_CSS_VARS.statusInfo, normalized.status.info);
  root.style.setProperty(THEME_CSS_VARS.statusInfoSoft, normalized.status.infoSoft);
  root.style.setProperty(THEME_CSS_VARS.statusInfoText, normalized.status.infoText);
  return normalized;
};
